// @flow

import _ from 'lodash';
import { getJSON, RSAA } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';

import apiService from 'src/services/api-service';
import {
  CONNECT_SHOPIFY_REQUEST,
  CONNECT_SHOPIFY_SUCCESS,
  CONNECT_SHOPIFY_FAILURE,
  UPDATE_TENANT_APP_REQUEST,
  UPDATE_TENANT_APP_SUCCESS,
  UPDATE_TENANT_APP_FAILURE,
  UPDATE_LOCAL_TENANT_APP,
  UPDATE_APP_SCOPES,
  UPDATE_FEATURE_SETTINGS,
  UPDATE_CURRENT_APP_INTEGRATION,
  UPDATE_CURRENT_APP_LANGUAGE_REQUEST,
  UPDATE_CURRENT_APP_LANGUAGE_SUCCESS,
  UPDATE_CURRENT_APP_LANGUAGE_FAILURE,
  UPDATE_CURRENT_APP_VERSION_REQUEST,
  UPDATE_CURRENT_APP_VERSION_SUCCESS,
  UPDATE_CURRENT_APP_VERSION_FAILURE,
  UPDATE_WATERMARK_REQUEST,
  UPDATE_WATERMARK_SUCCESS,
  UPDATE_WATERMARK_FAILURE,
  GET_TENANT_APP_GROUPS_REQUEST,
  GET_TENANT_APP_GROUPS_SUCCESS,
  GET_TENANT_APP_GROUPS_FAILURE,
  GET_REFRESH_TOKEN_STATUS_REQUEST,
  GET_REFRESH_TOKEN_STATUS_SUCCESS,
  GET_REFRESH_TOKEN_STATUS_FAILURE,
  UNAUTHORIZED_ACCESS,
  UPDATE_QUERY_PARAMS,
  UPDATE_SMART_STYLING_LIMITATION
} from 'stores/flow-type/oauth.type';
import { trackFSEvent } from 'src/integrations/fullStory';
import { APP_VERSION_OLD_VERSION } from 'src/constants/accounts';
import { GET_ACCESS_SCOPE_REQUEST, GET_ACCESS_SCOPE_FAILURE } from '../flow-type/common.type';

/**
 * connect to Shopify to get data
 * @return {object} The RSAA object for API middleware
 */
export const connectShopify = (queries: Object, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.connect.post-shopify-connect-v2', { queries }, [
    CONNECT_SHOPIFY_REQUEST,
    {
      type: CONNECT_SHOPIFY_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data, tenantId: queries.shop, host: queries.host };
      }
    },
    {
      type: CONNECT_SHOPIFY_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.OAuth.Actions.connectShopify'),
          i18n
        )
    }
  ]);
};

/**
 * connect to Shopify to get data
 * @return {object} The RSAA object for API middleware
 */
export const updateTenantApp = (body: Object, meta: Object, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.tenant-app.update-tenant-app', { body }, [
    UPDATE_TENANT_APP_REQUEST,
    {
      type: UPDATE_TENANT_APP_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      },
      meta
    },
    {
      type: UPDATE_TENANT_APP_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.OAuth.Actions.updateTenantApp'),
          i18n
        ),
      meta
    }
  ]);
};

/**
 * Update tenantApp locally without calling API
 * @return {object} Action object
 */
export const updateLocalTenantApp = data => {
  return {
    type: UPDATE_LOCAL_TENANT_APP,
    payload: { data }
  };
};

export const updateFeaturesAppSettings = (data: Object, featureName: string) => {
  return {
    type: UPDATE_FEATURE_SETTINGS,
    payload: { data, featureName }
  };
};

/**
 * Update status of oauth when 403/401
 * @return {object} The RSAA object for API middleware
 */
export const unauthorizedAccess = () => {
  return {
    type: UNAUTHORIZED_ACCESS,
    payload: {}
  };
};

/**
 * Update access scope for tenant app
 * @return {object} The RSAA object for API middleware
 */
export const updateAppScopes = accessScopes => {
  return {
    type: UPDATE_APP_SCOPES,
    payload: { accessScopes }
  };
};

export const getAppScopes = () => {
  return apiService.sendRequest('admin-v3.app-permission.get-scopes', {}, [
    GET_ACCESS_SCOPE_REQUEST,
    {
      type: UPDATE_APP_SCOPES,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return data;
      }
    },
    {
      type: GET_ACCESS_SCOPE_FAILURE
    }
  ]);
};

/**
 * Update current app  integration
 * @return {object} The RSAA object for API middleware
 */
export const updateCurrentAppIntegration = (appType, appName, appKey) => {
  return {
    type: UPDATE_CURRENT_APP_INTEGRATION,
    payload: { appType, appName, appKey }
  };
};

/**
 * Update current app language
 * @return {object} The RSAA object for API middleware
 */
export const updateCurrentAppLanguage = (body: Object, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.tenant-app.update-app-language', { body }, [
    UPDATE_CURRENT_APP_LANGUAGE_REQUEST,
    {
      type: UPDATE_CURRENT_APP_LANGUAGE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        // Track Full Story event
        trackFSEvent('Change Language', {
          feature: 'Language Settings',
          toLanguage: data?.appLanguage,
          fromLanguage: _.get(state, 'oauth.tenantApp.appLanguage', 'en')
        });
        return { data };
      }
    },
    {
      type: UPDATE_CURRENT_APP_LANGUAGE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.OAuth.Actions.updateCurrentAppLanguage'),
          i18n
        )
    }
  ]);
};

/**
 * Update current app version (turbo/old version)
 * @return {object} The RSAA object for API middleware
 */
export const updateAppVersion = (body: Object, i18n: I18n) => {
  const { appVersion } = body;

  return apiService.sendRequest('admin-v3.tenant-app.update-app-version', { body }, [
    UPDATE_CURRENT_APP_VERSION_REQUEST,
    {
      type: UPDATE_CURRENT_APP_VERSION_SUCCESS,
      payload: async (action, state, res) => {
        let data = null;
        if (appVersion === APP_VERSION_OLD_VERSION) {
          data = await processRes(
            res,
            'info',
            i18n.translate('AdminHTML.OAuth.Actions.updateCurrentAppVersionSuccessOld'),
            i18n
          );
        } else {
          data = await getJSON(res);
        }

        // Track Full Story event
        trackFSEvent('Change App Version', {
          feature: 'App Version Settings',
          toVersion: data?.appVersion,
          fromVersion: _.get(state, 'oauth.tenantApp.appVersion')
        });

        return { data, appVersion };
      }
    },
    {
      type: UPDATE_CURRENT_APP_VERSION_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.OAuth.Actions.updateCurrentAppVersionFailure'),
          i18n
        )
    }
  ]);
};

/**
 * Update current app version (turbo/old version)
 * @return {object} The RSAA object for API middleware
 */
export const updateWaterMark = (body: Object, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.tenant-app.update-watermark', { body }, [
    UPDATE_WATERMARK_REQUEST,
    {
      type: UPDATE_WATERMARK_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        // Track Full Story event
        trackFSEvent('Change water mark', {
          feature: 'Water mark Settings',
          toVersion: data?.appVersion,
          fromVersion: _.get(state, 'oauth.tenantApp.watermark')
        });

        return { data };
      }
    },
    {
      type: UPDATE_WATERMARK_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.OAuth.Actions.updateWatermarkFailure'),
          i18n
        )
    }
  ]);
};

/**
 * connect to app simulator to get data
 * @return {object} The RSAA object for API middleware
 */
export const connectSimulator = (queries: Object, i18n: I18n) => {
  const { auth0Token } = queries;
  return apiService.sendRequest(
    'admin-v3.connect.post-connect-simulator',
    { queries, auth0AccessToken: auth0Token },
    [
      CONNECT_SHOPIFY_REQUEST,
      {
        type: CONNECT_SHOPIFY_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data, tenantId: queries.shop, isSimulator: true };
        }
      },
      {
        type: CONNECT_SHOPIFY_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.OAuth.Actions.connectSimulator'),
            i18n
          )
      }
    ]
  );
};

/**
 * Get tenant app groups info
 * @return {object} The RSAA object for API middleware
 */
export const getTenantAppGroups = (queries: Object, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.tenant-app.groups', {}, [
    GET_TENANT_APP_GROUPS_REQUEST,
    {
      type: GET_TENANT_APP_GROUPS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data };
      }
    },
    {
      type: GET_TENANT_APP_GROUPS_FAILURE,
      // Silent: groups only decide whether WhatsApp support auto-shows and the reducer falls
      // back to no groups, so neither outcome may toast on every dashboard load
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.OAuth.Actions.getTenantGroups'),
          i18n,
          true
        )
    }
  ]);
};

/**
 * Check whether the shop's offline access token is dead and needs reauthorization
 * @return {object} The RSAA object for API middleware
 */
export const getRefreshTokenStatus = (i18n: I18n) => (dispatch: any, getState: any) => {
  const { authToken, reAuthToken } = getState().oauth;

  const action = apiService.sendRequest('admin-v3.tenant-app.refresh-token-status', {}, [
    GET_REFRESH_TOKEN_STATUS_REQUEST,
    {
      type: GET_REFRESH_TOKEN_STATUS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_REFRESH_TOKEN_STATUS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.OAuth.Actions.getRefreshTokenStatus'),
          i18n
        )
    }
  ]);

  // authToken may be cleared by another 401 response, fall back to the token kept for re-auth
  if (!authToken && reAuthToken) {
    const rsaa = action[RSAA];
    rsaa.headers = { ...rsaa.headers, 'x-bc-sf-filter-auth': reAuthToken };
  }

  return dispatch(action);
};

/**
 * Update query params
 * @return {object} The RSAA object for API middleware
 */
export const updateQueryParams = (queries: string) => {
  return {
    type: UPDATE_QUERY_PARAMS,
    payload: queries
  };
};

/**
 * Update smart styling limitation
 * @return {object} The RSAA object for API middleware
 */
export const updateSmartStylingLimitation = (settings: any) => {
  return {
    type: UPDATE_SMART_STYLING_LIMITATION,
    payload: settings
  };
};
